import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';

const ROLES = ['All','Primary','Secondary','Accounts','Marketing','Decision Maker'];

export default function ContactsPage() {
  const { data: clients=[], isLoading } = useQuery({ queryKey:['clients'], queryFn:()=>api.get('/clients').then(r=>r.data) });
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');

  const contacts = useMemo(() => {
    const list = [];
    clients.forEach(c => {
      const push = (role, name, phone, email, birthday=null) => {
        if (!name) return;
        list.push({ clientId:c.id, clientName:c.name, role, name, phone:phone||'', email:email||'', birthday });
      };
      push('Primary', c.contact_person, c.phone, c.email);
      push('Secondary', c.secondary_contact_name, c.secondary_contact_phone, c.secondary_contact_email);
      push('Accounts', c.accounts_contact_name, c.accounts_contact_phone, c.accounts_contact_email);
      push('Marketing', c.marketing_contact_name, c.marketing_contact_phone, c.marketing_contact_email);
      push('Decision Maker', c.decision_maker_name, c.decision_maker_phone, c.decision_maker_email, c.decision_maker_birthday);
    });
    return list;
  }, [clients]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return contacts.filter(ct => {
      if (roleFilter !== 'All' && ct.role !== roleFilter) return false;
      if (q) return ct.name.toLowerCase().includes(q) || ct.clientName.toLowerCase().includes(q) || ct.email.toLowerCase().includes(q);
      return true;
    });
  }, [contacts, search, roleFilter]);

  const roleBg = { Primary:'#EFF6FF', Secondary:'#F0FDF4', Accounts:'#FFFBEB', Marketing:'#FDF4FF', 'Decision Maker':'#FFF1F2' };
  const roleColor = { Primary:'#1D4ED8', Secondary:'#15803D', Accounts:'#B45309', Marketing:'#7E22CE', 'Decision Maker':'#BE123C' };

  return (
    <div>
      <div className="section-head">
        <div>
          <h2>Contacts</h2>
          <div style={{fontSize:12,color:'var(--text2)',marginTop:2}}>{filtered.length} contact{filtered.length!==1?'s':''}</div>
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, company, email…" style={{fontSize:12,padding:'6px 10px',width:220}}/>
      </div>

      <div style={{display:'flex',gap:6,marginBottom:16,flexWrap:'wrap'}}>
        {ROLES.map(r => (
          <button key={r} className={roleFilter===r?'btn-primary btn-sm':'btn btn-sm'} onClick={()=>setRoleFilter(r)}>{r}</button>
        ))}
      </div>

      {isLoading && <div className="empty">Loading contacts…</div>}
      {!isLoading && !filtered.length && <div className="empty">{search||roleFilter!=='All' ? 'No contacts match your filters.' : 'No contacts yet — add them via Clients → Edit.'}</div>}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12}}>
        {filtered.map((ct, i) => (
          <div key={i} className="card" style={{padding:'14px 16px'}}>
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8,marginBottom:8}}>
              <div style={{fontWeight:700,fontSize:14,color:'var(--text1)'}}>{ct.name}</div>
              <span style={{
                fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:10,flexShrink:0,
                background:roleBg[ct.role]||'#F1F5F9', color:roleColor[ct.role]||'#475569',
              }}>{ct.role}</span>
            </div>
            <div style={{fontSize:12,color:'var(--accent)',fontWeight:500,marginBottom:8}}>{ct.clientName}</div>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              {ct.phone && <div style={{fontSize:12,color:'var(--text2)'}}>📞 {ct.phone}</div>}
              {ct.email && <div style={{fontSize:12,color:'var(--text2)',wordBreak:'break-all'}}>✉ {ct.email}</div>}
              {ct.birthday && (
                <div style={{fontSize:11,color:'#BE123C',marginTop:2}}>
                  🎂 {new Date(ct.birthday+'T00:00:00').toLocaleDateString('en-IN',{day:'numeric',month:'long'})}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
